import { useQueryClient } from '@tanstack/react-query'
import { DatePicker, Form, InputNumber, message, Popconfirm, Popover, Switch, theme, Tooltip } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useRateManagement } from '@api/useFreight/useFreight'
import type { Context, GridApi } from 'ag-grid-community'
import { isPercentageFeeType } from '@/components/Freight/shared'

// Define the types of rates that can be managed
export type RateType = 'surcharge' | 'pointToPoint' | 'manualAccessorial' | 'automaticAccessorial'

// Define the base interface for all rate types
export interface BaseRate {
  _id: string
  id?: string
  disabled?: boolean
  rates?: Array<{
    effective_from?: string
    effective_from_date?: string
    rate?: number
    surcharge?: number
    id?: string
  }>
  type?: string
  fee_type?: {
    type: string
  }
  name?: string
  distance?: number
}

// Define the props for the ActiveStatusRenderer component
export interface ActiveStatusRendererProps<T extends BaseRate> {
  value: boolean
  data: T
  rateType: RateType
  onSuccess?: () => void
  context?: Context
  api?: GridApi
}

/**
 * A unified component for rendering active/inactive switches with popovers
 * Used across surcharge, point-to-point, manual accessorial, and automatic accessorial pages
 */
export function ActiveStatusRenderer<T extends BaseRate>({
  value,
  data,
  rateType,
  onSuccess,
  api,
}: ActiveStatusRendererProps<T>) {
  const { token } = theme.useToken()
  const queryClient = useQueryClient()
  const [popoverVisible, setPopoverVisible] = React.useState(false)
  const [popconfirmVisible, setPopconfirmVisible] = React.useState(false)
  const [selectedDate, setSelectedDate] = React.useState<dayjs.Dayjs | null>(dayjs().startOf('day'))
  const [selectedRate, setSelectedRate] = React.useState<number | null>(null)
  const [form] = Form.useForm()

  // Get the rate management hooks
  const {
    useDisableSurcharge,
    useCreateSurchargeRate,
    useTogglePointToPointStatus,
    useCreatePointToPointRate,
    useDisableManualAccessorial,
    useCreateManualAccessorialRate,
    useDisableAutomaticAccessorial,
    useCreateAutomaticAccessorialRate,
  } = useRateManagement()

  // Initialize mutations
  const disableSurchargeMutation = useDisableSurcharge()
  const createSurchargeRateMutation = useCreateSurchargeRate()
  const togglePointToPointStatusMutation = useTogglePointToPointStatus()
  const createPointToPointRateMutation = useCreatePointToPointRate()
  const disableManualAccessorialMutation = useDisableManualAccessorial()
  const createManualAccessorialRateMutation = useCreateManualAccessorialRate()
  const disableAutomaticAccessorialMutation = useDisableAutomaticAccessorial()
  const createAutomaticAccessorialRateMutation = useCreateAutomaticAccessorialRate()

  // Check if any mutation is pending
  const isLoading = React.useMemo(() => {
    return (
      disableSurchargeMutation.isPending ||
      createSurchargeRateMutation.isPending ||
      togglePointToPointStatusMutation.isPending ||
      createPointToPointRateMutation.isPending ||
      disableManualAccessorialMutation.isPending ||
      createManualAccessorialRateMutation.isPending ||
      disableAutomaticAccessorialMutation.isPending ||
      createAutomaticAccessorialRateMutation.isPending
    )
  }, [
    disableSurchargeMutation.isPending,
    createSurchargeRateMutation.isPending,
    togglePointToPointStatusMutation.isPending,
    createPointToPointRateMutation.isPending,
    disableManualAccessorialMutation.isPending,
    createManualAccessorialRateMutation.isPending,
    disableAutomaticAccessorialMutation.isPending,
    createAutomaticAccessorialRateMutation.isPending,
  ])

  // Determine if this is a percentage type rate
  const isPercentageType = React.useMemo(() => {
    if (rateType === 'surcharge') {
      return data.type === 'Percentage'
    }
    if (rateType === 'manualAccessorial' || rateType === 'automaticAccessorial') {
      const feeType = data.fee_type?.type || ''
      return feeType.includes('%')
    }
    return false
  }, [data, rateType])

  // Get the precision based on rate type
  const precision = isPercentageType ? 3 : 5

  // Memoize the formatter function to avoid recreating it on each render
  const formatRate = React.useCallback(
    (val: number | string | undefined) => {
      if (val === undefined || val === null) return isPercentageType ? '0.00%' : '$0.00'
      const numValue = Number(val).toFixed(precision)
      return isPercentageType ? `${numValue}%` : `$${numValue}`
    },
    [isPercentageType, precision]
  )

  // Get the last non-zero rate
  const getLastNonZeroRate = React.useCallback(() => {
    if (!data.rates || data.rates.length === 0) return 0

    // Sort rates by effective_from date in descending order
    const sortedRates = [...data.rates].sort(
      (a, b) => new Date(b.effective_from as string).getTime() - new Date(a.effective_from as string).getTime()
    )

    // Find the last rate that wasn't zero
    const lastNonZeroRate = sortedRates.find((rate) => {
      if (rateType === 'surcharge') {
        return rate.surcharge !== 0
      }
      return rate.rate !== 0
    })

    if (!lastNonZeroRate) return 0

    // For percentage types, convert to percentage for display
    const rate = rateType === 'surcharge' ? lastNonZeroRate.surcharge || 0 : lastNonZeroRate.rate || 0
    return isPercentageType ? rate * 100 : rate
  }, [data.rates, rateType, isPercentageType])

  // Update selectedRate when popover opens
  React.useEffect(() => {
    if (popoverVisible && value) {
      setSelectedRate(getLastNonZeroRate())
      form.setFieldsValue({
        rate: getLastNonZeroRate(),
        effective_date: selectedDate,
      })
    }
  }, [popoverVisible, value, getLastNonZeroRate, form, selectedDate])

  // Handle toggle action
  const handleToggle = React.useCallback(async () => {
    try {
      if (value) {
        // If currently disabled, enable it
        // Validate the form first
        try {
          await form.validateFields()
        } catch (errorInfo) {
          return // Stop if validation fails
        }

        const formValues = form.getFieldsValue()
        const currentDate = formValues.effective_date || dayjs().startOf('day')
        const currentRate = formValues.rate

        // Convert percentage back to decimal for API if needed
        const apiRate = isPercentageType ? currentRate / 100 : currentRate

        // Check if there's an existing rate for today
        const today = dayjs().startOf('day')
        const existingRate = data?.rates?.find((rate) => {
          const rateDate = dayjs(rate.effective_from || rate.effective_from_date).startOf('day')
          return rateDate.isSame(today)
        })

        // Handle rate creation based on rate type
        if (rateType === 'surcharge') {
          // Create the new active rate
          await createSurchargeRateMutation.mutateAsync({
            surcharge_id: data._id,
            effective_date: currentDate.format('YYYY-MM-DD'),
            surcharge: apiRate,
          })
        } else if (rateType === 'pointToPoint') {
          await createPointToPointRateMutation.mutateAsync({
            point_to_point_id: data._id,
            effective_date: currentDate.format('YYYY-MM-DD'),
            effective_to_date: null,
            rate: apiRate,
            distance: data.distance,
          })
        } else if (rateType === 'manualAccessorial') {
          await createManualAccessorialRateMutation.mutateAsync({
            accessorial_id: data._id,
            effective_from_date: currentDate.format('YYYY-MM-DD'),
            rate: apiRate,
          })
        } else if (rateType === 'automaticAccessorial') {
          await createAutomaticAccessorialRateMutation.mutateAsync({
            accessorial_id: data._id,
            effective_from_date: currentDate.format('YYYY-MM-DD'),
            effective_to_date: null,
            rate: apiRate,
          })
        }

        const actionType = 'enabled'
        message.success(`${getItemName()} ${actionType} successfully`)
      } else {
        // If currently enabled, disable it
        // For disabling, we might not need the form values directly,
        // but we still need the effective date. We can keep using selectedDate for simplicity here or get it from form if needed.
        const disableDate = selectedDate || dayjs().startOf('day') // Use selectedDate state or default
        if (!disableDate) return

        // Handle disabling based on rate type
        if (rateType === 'surcharge') {
          await disableSurchargeMutation.mutateAsync({
            surcharge_id: data._id,
            effective_from: disableDate.format('YYYY-MM-DD'),
          })
        } else if (rateType === 'pointToPoint') {
          await togglePointToPointStatusMutation.mutateAsync({
            point_to_point_id: data._id,
            effective_date: disableDate.format('YYYY-MM-DD'),
            current_status: false,
          })
        } else if (rateType === 'manualAccessorial') {
          await disableManualAccessorialMutation.mutateAsync({
            accessorial_id: data._id,
            as_of: disableDate.format('YYYY-MM-DD'),
          })
        } else if (rateType === 'automaticAccessorial') {
          await disableAutomaticAccessorialMutation.mutateAsync({
            accessorial_id: data._id,
            as_of: disableDate.format('YYYY-MM-DD'),
          })
        }

        const actionType = 'disabled'
        message.success(`${getItemName()} ${actionType} successfully`)
      }

      setPopoverVisible(false)
      setPopconfirmVisible(false)

      // Refresh the data
      await refreshData()

      if (onSuccess) {
        onSuccess()
        setTimeout(() => {
          api?.refreshCells()
          api?.redrawRows()
        }, 1000)
      }
    } catch (error) {
      console.error(`Error toggling ${rateType} status:`, error)
      message.error(`Failed to ${value ? 'enable' : 'disable'} ${getItemName()}`)
    }
  }, [
    value,
    selectedDate,
    isPercentageType,
    rateType,
    data,
    onSuccess,
    disableSurchargeMutation,
    createSurchargeRateMutation,
    togglePointToPointStatusMutation,
    createPointToPointRateMutation,
    disableManualAccessorialMutation,
    createManualAccessorialRateMutation,
    disableAutomaticAccessorialMutation,
    createAutomaticAccessorialRateMutation,
    form,
  ])

  // Helper function to get the item name for messages
  const getItemName = () => {
    if (rateType === 'surcharge') return 'Surcharge'
    if (rateType === 'pointToPoint') return 'Point to point rate'
    if (rateType === 'manualAccessorial') return data.name || 'Manual accessorial'
    if (rateType === 'automaticAccessorial') return data.name || 'Automatic accessorial'
    return 'Item'
  }

  // Helper function to refresh data
  const refreshData = async () => {
    if (rateType === 'surcharge') {
      await queryClient.invalidateQueries({ queryKey: ['surcharges'] })
    } else if (rateType === 'pointToPoint') {
      queryClient.invalidateQueries({ queryKey: ['ratebook/p2p'] })
    } else if (rateType === 'manualAccessorial') {
      await queryClient.invalidateQueries({
        queryKey: ['manual_accessorials'],
      })
    } else if (rateType === 'automaticAccessorial') {
      await queryClient.invalidateQueries({
        queryKey: ['automaticAccessorials'],
      })
    }

    if (api) {
      api.refreshCells()
    }
  }

  // Create popover content with date picker and rate input
  const popoverContent = (
    <Form form={form} layout='vertical' initialValues={{ effective_date: selectedDate, rate: selectedRate }}>
      <Vertical gap={16} style={{ padding: '8px', minWidth: '250px' }}>
        <Form.Item
          name='effective_date'
          label={`Select effective date for ${value ? 'enabling' : 'disabling'} this ${getItemName().toLowerCase()}:`}
          rules={[{ required: true, message: 'Please select an effective date!' }]}
          style={{ marginBottom: 0 }}
        >
          <DatePicker
            value={selectedDate}
            onChange={(date) => {
              setSelectedDate(date)
              form.setFieldsValue({ effective_date: date })
            }}
            style={{ width: '100%' }}
          />
        </Form.Item>

        {value && (
          <Form.Item
            name='rate'
            label={rateType === 'surcharge' ? `Enter ${isPercentageType ? 'percentage' : 'per mile'} rate:` : 'Rate:'}
            rules={[
              { required: true, message: 'Please enter a rate!' },
              {
                validator: (_, formValue) => {
                  const isAccessorial = rateType === 'manualAccessorial' || rateType === 'automaticAccessorial'
                  const isEnteredNegative = formValue !== undefined && formValue !== null && Number(formValue) < 0
                  if (!isAccessorial && isEnteredNegative) {
                    return Promise.reject(new Error('Rate must be greater than 0'))
                  }
                  return Promise.resolve()
                },
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              style={{ width: '100%' }}
              step={1.0}
              precision={precision}
              formatter={formatRate}
              parser={(val) => Number.parseFloat(val?.replace(/[$%\s,]/g, '') || '0')}
            />
          </Form.Item>
        )}
        <Horizontal justifyContent='flex-end' gap={8}>
          <GraviButton appearance='text' onClick={() => setPopoverVisible(false)}>
            Cancel
          </GraviButton>
          <GraviButton onClick={handleToggle} loading={isLoading}>
            Confirm
          </GraviButton>
        </Horizontal>
      </Vertical>
    </Form>
  )

  // Render the Switch with either Popover or Popconfirm based on the current state
  if (value) {
    // Item is disabled, show Popover for enabling
    return (
      <Popover
        content={popoverContent}
        title={`${value ? 'Enable' : 'Disable'} ${getItemName()}`}
        trigger='click'
        open={popoverVisible}
        onOpenChange={setPopoverVisible}
      >
        <Tooltip title={`This ${getItemName().toLowerCase()} will not generate any new line items`}>
          <Switch
            checked={false}
            onChange={() => setPopoverVisible(true)}
            checkedChildren='Active'
            unCheckedChildren='Inactive'
            loading={isLoading}
          />
        </Tooltip>
      </Popover>
    )
  }

  // Item is enabled, show Popconfirm for disabling
  return (
    <Popconfirm
      title={`Disable ${getItemName()}`}
      description={`This will disable the ${getItemName().toLowerCase()}. Continue?`}
      onConfirm={handleToggle}
      okText='Yes'
      cancelText='No'
      open={popconfirmVisible}
      onCancel={() => setPopconfirmVisible(false)}
    >
      <Tooltip title={`This ${getItemName().toLowerCase()} is actively generating line items`}>
        <Switch
          checked={true}
          onChange={() => setPopconfirmVisible(true)}
          checkedChildren='Active'
          unCheckedChildren='Inactive'
          loading={isLoading}
        />
      </Tooltip>
    </Popconfirm>
  )
}
